import { useEffect, useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { EditorView } from '@codemirror/view';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { yCollab } from 'y-codemirror.next';

interface ScreenplayEditorProps {
    scriptId: string;
    initialContent: string;
    onContentChange?: (content: string) => void;
}

export function ScreenplayEditor({ scriptId, initialContent, onContentChange }: ScreenplayEditorProps) {
    const [value, setValue] = useState(initialContent);
    const ydocRef = useRef<Y.Doc | null>(null);
    const providerRef = useRef<WebsocketProvider | null>(null);

    useEffect(() => {
        // Initialize Yjs document
        const ydoc = new Y.Doc();
        const ytext = ydoc.getText('codemirror');
        ydocRef.current = ydoc;

        // Set initial content if empty
        if (ytext.toString() === '') {
            ytext.insert(0, initialContent);
        }

        // Connect to WebSocket provider (Durable Object)
        const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/collab/${scriptId}`;
        const provider = new WebsocketProvider(wsUrl, scriptId, ydoc);
        providerRef.current = provider;

        provider.on('status', (event: any) => {
            console.log('WebSocket status:', event.status);
        });

        return () => {
            provider?.destroy();
            ydoc?.destroy();
        };
    }, [scriptId, initialContent]);

    // Custom theme for screenplay
    const screenplayTheme = EditorView.theme({
        '&': {
            backgroundColor: 'transparent',
            color: 'var(--color-text-primary)',
            height: '100%',
        },
        '.cm-content': {
            fontFamily: 'var(--font-mono)',
            fontSize: '12pt',
            lineHeight: '1.5',
            caretColor: 'var(--color-primary)',
            padding: '2rem 0',
        },
        '.cm-line': {
            color: 'var(--color-text-primary)',
        },
        '.cm-cursor, .cm-dropCursor': {
            borderLeftColor: 'var(--color-primary)',
        },
        '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
            backgroundColor: 'rgba(138, 31, 42, 0.3)',
        },
        '.cm-activeLine': {
            backgroundColor: 'rgba(138, 31, 42, 0.05)',
        },
        '.cm-gutters': {
            backgroundColor: 'transparent',
            border: 'none',
        },
    });

    const handleChange = (val: string) => {
        setValue(val);
        if (onContentChange) {
            onContentChange(val);
        }
    };

    return (
        <div
            className="screenplay-editor"
            style={{
                height: '100%',
                backgroundColor: 'var(--color-bg-primary)',
                overflow: 'auto',
            }}
        >
            <CodeMirror
                value={value}
                height="100%"
                theme={screenplayTheme}
                onChange={handleChange}
                extensions={[
                    EditorView.lineWrapping,
                    ...(ydocRef.current ? [yCollab(ydocRef.current.getText('codemirror'), providerRef.current?.awareness)] : []),
                ]}
                basicSetup={{
                    lineNumbers: false,
                    foldGutter: false,
                    highlightActiveLineGutter: false,
                }}
            />
        </div>
    );
}
